import { useMemo } from 'react';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  DEFAULT_SCREEN_OPTIONS,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useGiftCardsTabStackOptions = () => {
  const { currentBreakpoint } = useResponsive();
  const titles = useRouteTitles();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(
    () => ({
      ...DEFAULT_SCREEN_OPTIONS,
      ...SCREEN_HEADER_OPTIONS,
      headerShown: currentBreakpoint.isXS,
    }),
    [currentBreakpoint.isXS],
  );

  /// ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Tabs ─────────────────────────────────────────────────

      Home: {
        title: titles.giftCardsTabTitle,
        customDocumentTitle: titles.giftCardsTabCustomDocumentTitle,
        headerShown: false,
        navbarLayout: 'static-with-border',
      },
      Checkout: {
        title: titles.giftCardCheckoutTitle,
        customDocumentTitle: titles.giftCardCheckoutCustomDocumentTitle,
        navbarLayout: 'static-with-border',
      },
      Confirmation: {
        title: titles.giftCardConfirmationTitle,
        customDocumentTitle: titles.giftCardConfirmationCustomDocumentTitle,
        navbarLayout: 'static-with-border',
      },
      Redeem: {
        title: titles.redeemGiftCardTitle,
        customDocumentTitle: titles.giftCardRedemptionCustomDocumentTitle,
        headerShown: !isNavbarV2Enabled && currentBreakpoint.isXS,
        navbarLayout: 'static-with-border',
      },
    } satisfies ScreenOptionsMap;
  }, [
    stackOptions,
    titles.giftCardCheckoutTitle,
    titles.giftCardCheckoutCustomDocumentTitle,
    titles.giftCardConfirmationTitle,
    titles.giftCardConfirmationCustomDocumentTitle,
    titles.giftCardsTabTitle,
    titles.giftCardsTabCustomDocumentTitle,
    titles.redeemGiftCardTitle,
    titles.giftCardRedemptionCustomDocumentTitle,
    currentBreakpoint.isXS,
    isNavbarV2Enabled,
  ]);
};
